import { useState } from 'react';
import logoIcon from '../../assets/images/standalone_logo.png';
import logoFull from '../../assets/images/logo_aktivity.png';

/**
 * Q&A Activity Component
 * Clean UI design with question submission and voting
 */

const QAActivity = ({
  activity,
  sessionCode,
  sessionName = 'Live Session',
  trainerName = 'Trainer',
  timeRemaining,
  initialQuestions = [],
  onQuestionSubmitted,
  onBackToList,
}) => {
  const [qaQuestion, setQaQuestion] = useState('');
  const [qaQuestions, setQaQuestions] = useState(initialQuestions);
  const [userVotes, setUserVotes] = useState({});
  const [sortBy, setSortBy] = useState('upvotes');
  const [hasSubmittedQuestion, setHasSubmittedQuestion] = useState(false);

  // Format time as M:SS
  const formatTime = (seconds) => {
    if (seconds === undefined || seconds === null || isNaN(seconds)) {
      return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate timer progress - use actual timeLimit from activity
  const maxTime = activity?.timerEnabled === true
    ? (activity.timeLimit || activity.config?.timeLimit || 0)
    : 0;
  const timerEnabled = maxTime > 0;

  const handleSubmitQuestion = () => {
    if (qaQuestion.trim().length >= 10) {
      const newQuestion = {
        id: Date.now(),
        text: qaQuestion,
        author: 'You',
        upvotes: 0,
        downvotes: 0,
        voted: null,
        timestamp: new Date(),
      };
      setQaQuestions([newQuestion, ...qaQuestions]);
      setQaQuestion('');

      if (!hasSubmittedQuestion && onQuestionSubmitted) {
        setHasSubmittedQuestion(true);
        onQuestionSubmitted();
      }
    }
  };

  const handleVote = (questionId, voteType) => {
    setQaQuestions(
      qaQuestions.map((q) => {
        if (q.id === questionId) {
          const currentVote = userVotes[questionId];
          let newUpvotes = q.upvotes;
          let newDownvotes = q.downvotes;

          if (currentVote === voteType) {
            if (voteType === 'up') newUpvotes--;
            else newDownvotes--;
            setUserVotes({ ...userVotes, [questionId]: null });
          } else {
            if (currentVote === 'up') newUpvotes--;
            if (currentVote === 'down') newDownvotes--;
            if (voteType === 'up') newUpvotes++;
            else newDownvotes++;
            setUserVotes({ ...userVotes, [questionId]: voteType });
          }

          return { ...q, upvotes: newUpvotes, downvotes: newDownvotes };
        }
        return q;
      })
    );
  };

  const sortedQuestions = [...qaQuestions].sort((a, b) => {
    if (sortBy === 'upvotes') {
      return b.upvotes - a.upvotes;
    }
    return new Date(b.timestamp) - new Date(a.timestamp);
  });

  const questionsRemaining = 3 - qaQuestions.filter((q) => q.author === 'You').length;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {onBackToList && (
                <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <div className="flex items-center">
                <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-medium text-green-700">Live</span>
              </div>
              {sessionCode && (
                <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                  <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Activity Type Heading */}
      <div className="bg-primary-50 px-6 py-3">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-lg font-bold text-primary-600 tracking-wide">Q&A</h2>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 lg:px-6 py-6 lg:py-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Timer Section - Mobile (shown at top on mobile) */}
          {timerEnabled && (
          <div className="lg:hidden order-first">
            <div className="flex flex-row items-center gap-4 mb-6 p-4 bg-neutral-50 rounded-xl">
              <div className="relative w-20 h-20">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 96 96">
                  <circle cx="48" cy="48" r="40" stroke="#e5e7eb" strokeWidth="6" fill="none" />
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    stroke="#ff8a00"
                    strokeWidth="6"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 40}`}
                    strokeDashoffset={`${2 * Math.PI * 40 * (1 - (timeRemaining / maxTime))}`}
                    className="transition-all duration-1000"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg font-bold text-primary-600">{formatTime(timeRemaining)}</span>
                </div>
              </div>
              <div className="flex-1">
                <span className="text-sm text-neutral-500">Time Remaining</span>
                <div className="text-xs text-neutral-400 mt-1">{sortedQuestions.length} questions • {questionsRemaining} remaining</div>
              </div>
            </div>
          </div>
          )}

          {/* Left Side - Questions */}
          <div className="flex-1 order-2 lg:order-1">
            {/* Title */}
            <h1 className="text-xl lg:text-3xl font-bold text-neutral-900 mb-3 lg:mb-4 leading-tight">
              {activity.title}
            </h1>

            {activity.question && (
              <p className="text-base lg:text-lg text-neutral-600 mb-4 lg:mb-6">{activity.question}</p>
            )}

            {/* Ask Question Card */}
            <div className="bg-neutral-50 rounded-xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-900">Ask a Question</h3>
                  <p className="text-sm text-neutral-500">{questionsRemaining} questions remaining</p>
                </div>
              </div>

              <textarea
                value={qaQuestion}
                onChange={(e) => setQaQuestion(e.target.value)}
                placeholder="Type your question here..."
                rows={3}
                maxLength={200}
                className="w-full px-4 py-3 text-neutral-900 bg-white border-2 border-neutral-200 rounded-xl focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 resize-none transition-all"
              />

              <div className="flex items-center justify-between mt-3">
                <p className="text-xs text-neutral-500">{qaQuestion.length}/200 (min. 10 characters)</p>
                <button
                  onClick={handleSubmitQuestion}
                  disabled={qaQuestion.length < 10 || questionsRemaining <= 0}
                  className="px-6 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-neutral-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors"
                >
                  Submit Question
                </button>
              </div>
            </div>

            {/* Questions List */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-neutral-900 flex items-center gap-2">
                  <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                  </svg>
                  Questions ({sortedQuestions.length})
                </h3>
                <select
                  className="text-sm border border-neutral-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-primary-500"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="upvotes">Most Upvoted</option>
                  <option value="recent">Most Recent</option>
                </select>
              </div>

              {sortedQuestions.length === 0 ? (
                <div className="text-center py-12 bg-neutral-50 rounded-xl">
                  <svg className="w-12 h-12 text-neutral-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-neutral-500 font-medium">No questions yet</p>
                  <p className="text-sm text-neutral-400">Be the first to ask!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sortedQuestions.map((q) => (
                    <div
                      key={q.id}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        q.author === 'You'
                          ? 'border-primary-200 bg-primary-50'
                          : 'border-neutral-200 bg-white hover:border-neutral-300'
                      }`}
                    >
                      <p className="text-neutral-900 mb-3">{q.text}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-neutral-500">{q.author}</span>
                          {q.author === 'You' && (
                            <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                              Your question
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleVote(q.id, 'up')}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors ${
                              userVotes[q.id] === 'up'
                                ? 'bg-green-100 text-green-700'
                                : 'hover:bg-neutral-100 text-neutral-600'
                            }`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                            <span className="text-sm font-semibold">{q.upvotes}</span>
                          </button>
                          <button
                            onClick={() => handleVote(q.id, 'down')}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors ${
                              userVotes[q.id] === 'down'
                                ? 'bg-red-100 text-red-700'
                                : 'hover:bg-neutral-100 text-neutral-600'
                            }`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                            <span className="text-sm font-semibold">{q.downvotes}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Timer & Info (Desktop only) */}
          <div className="hidden lg:block w-64 flex-shrink-0 order-3 lg:order-2">
            <div className="sticky top-8">
              {/* Circular Timer */}
              {timerEnabled && (
              <div className="flex flex-col items-center mb-8">
                <div className="relative w-24 h-24">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 96 96">
                    <circle cx="48" cy="48" r="40" stroke="#e5e7eb" strokeWidth="6" fill="none" />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="#ff8a00"
                      strokeWidth="6"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 40}`}
                      strokeDashoffset={`${2 * Math.PI * 40 * (1 - (timeRemaining / maxTime))}`}
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-primary-600">{formatTime(timeRemaining)}</span>
                  </div>
                </div>
                <span className="text-sm text-neutral-500 mt-2">Time Remaining</span>
              </div>
              )}

              {/* Activity Info */}
              <div className="bg-neutral-50 rounded-xl p-4">
                <h3 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Activity Info
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Session:</span>
                    <span className="font-medium text-neutral-700">{sessionCode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Type:</span>
                    <span className="font-medium text-neutral-700">Q&A</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Questions:</span>
                    <span className="font-medium text-neutral-700">{sortedQuestions.length}</span>
                  </div>
                </div>
              </div>

              {/* Your Stats */}
              <div className="mt-4 bg-primary-50 rounded-xl p-4 border border-primary-100">
                <h4 className="font-semibold text-primary-900 mb-3">Your Activity</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-primary-700">Questions asked:</span>
                    <span className="font-medium text-primary-900">
                      {qaQuestions.filter((q) => q.author === 'You').length}/3
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-primary-700">Votes cast:</span>
                    <span className="font-medium text-primary-900">
                      {Object.values(userVotes).filter(Boolean).length}
                    </span>
                  </div>
                </div>
              </div>

              {/* Tips */}
              <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                <div className="flex gap-2">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-blue-800">Tips</p>
                    <p className="text-xs text-blue-700 mt-1">Vote for questions you want the trainer to answer first!</p>
                  </div>
                </div>
              </div>

              {/* Anonymous Notice */}
              <div className="mt-4 flex items-center gap-2 text-sm text-neutral-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>Questions are anonymous</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QAActivity;
